import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Loading } from '../components/Loading';
import { showToast } from '../utils/toast';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { financeService, FinanceData, FinanceSummary } from '../services/financeService';
import { exportService } from '../services/exportService';
import { expenseService } from '../services/expenseService';
import { ExpenseSummary } from '../types';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/currency';

export const Finance: React.FC = () => {
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'month' | 'year'>('month');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [rangeData, setRangeData] = useState<FinanceData | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [expenseSummary, setExpenseSummary] = useState<ExpenseSummary | null>(null);
  const [expensesLoading, setExpensesLoading] = useState(false);

  useEffect(() => {
    loadSummary();
    loadCategories();
    loadExpenseSummary();
  }, []);

  useEffect(() => {
    loadExpenseSummary();
  }, [selectedPeriod, selectedDate, selectedMonth, selectedYear]);

  const loadCategories = async () => {
    // Only show the three categories that are sold: Tyre, Battery, Rims
    setCategories(['Tyre', 'Battery', 'Rims']);
  };

  const loadExpenseSummary = async () => {
    try {
      setExpensesLoading(true);
      const params: any = {};
      
      if (selectedPeriod === 'today') {
        params.start_date = selectedDate;
        params.end_date = selectedDate;
      } else if (selectedPeriod === 'month') {
        const [year, month] = selectedMonth.split('-');
        const start = new Date(parseInt(year), parseInt(month) - 1, 1);
        const end = new Date(parseInt(year), parseInt(month), 0);
        params.start_date = format(start, 'yyyy-MM-dd');
        params.end_date = format(end, 'yyyy-MM-dd');
      } else if (selectedPeriod === 'year') {
        params.start_date = `${selectedYear}-01-01`;
        params.end_date = `${selectedYear}-12-31`;
      }
      
      const response = await expenseService.getExpenseSummary(params);
      if (response.success && response.data) {
        setExpenseSummary(response.data);
      }
    } catch (err) {
      console.error('Failed to load expense summary:', err);
    } finally {
      setExpensesLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPeriod === 'today') {
      loadDailyFinance(selectedDate);
    } else if (selectedPeriod === 'month') {
      loadMonthlyFinance(selectedMonth);
    } else if (selectedPeriod === 'year') {
      loadYearlyFinance(parseInt(selectedYear));
    }
  }, [selectedPeriod, selectedDate, selectedMonth, selectedYear, selectedCategory]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await financeService.getFinanceSummary();
      if (response.success && response.data) {
        setSummary(response.data);
      } else {
        showToast.error(response.message || 'Failed to load finance summary');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to load finance summary');
    } finally {
      setLoading(false);
    }
  };

  const loadDailyFinance = async (date: string) => {
    try {
      setLoading(true);
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const response = await financeService.getDailyFinance(date, category);
      if (response.success && response.data) {
        // Update summary with daily data
        if (summary) {
          setSummary({
            ...summary,
            today: {
              ...response.data,
              date: response.data.date || date,
              total_sales: response.data.total_sales || 0,
            },
          });
        }
      }
    } catch (err) {
      console.error('Failed to load daily finance:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyFinance = async (month: string) => {
    try {
      setLoading(true);
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const response = await financeService.getMonthlyFinance(month, category);
      if (response.success && response.data) {
        if (summary) {
          setSummary({
            ...summary,
            this_month: {
              ...response.data,
              period: response.data.period || month,
              total_sales: response.data.total_sales || 0,
            },
          });
        }
      }
    } catch (err) {
      console.error('Failed to load monthly finance:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadYearlyFinance = async (year: number) => {
    try {
      setLoading(true);
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const response = await financeService.getYearlyFinance(year, category);
      if (response.success && response.data) {
        if (summary) {
          setSummary({
            ...summary,
            this_year: {
              ...response.data,
              year: response.data.year || year,
              total_sales: response.data.total_sales || 0,
            },
          });
        }
      }
    } catch (err) {
      console.error('Failed to load yearly finance:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDateRange = async () => {
    if (!startDate || !endDate) {
      showToast.error('Please select both start and end dates');
      return;
    }

    try {
      setLoading(true);
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const response = await financeService.getFinanceRange(startDate, endDate, category);
      if (response.success && response.data) {
        setRangeData(response.data);
      } else {
        showToast.error(response.message || 'Failed to load finance data');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = (): FinanceData | null => {
    if (!summary) return null;

    switch (selectedPeriod) {
      case 'today':
        return summary.today;
      case 'month':
        return summary.this_month;
      case 'year':
        return summary.this_year;
      default:
        return null;
    }
  };

  const getChartData = () => {
    if (!rangeData?.daily_breakdown) return [];

    return rangeData.daily_breakdown.map((day) => ({
      date: format(new Date(day.date!), 'MMM dd'),
      revenue: day.total_revenue,
      cost: day.total_cost,
      profit: day.total_profit || (day.profit_loss > 0 ? day.profit_loss : 0),
      loss: day.total_loss || (day.profit_loss < 0 ? Math.abs(day.profit_loss) : 0),
    }));
  };


  const handleExportPDF = async () => {
    try {
      setExporting('pdf');

      const params: any = { type: selectedPeriod };
      if (selectedPeriod === 'today') {
        params.date = selectedDate;
      } else if (selectedPeriod === 'month') {
        params.month = selectedMonth;
      } else if (selectedPeriod === 'year') {
        params.year = parseInt(selectedYear);
      }
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const blob = await exportService.exportFinancePDF(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const categorySuffix = selectedCategory !== 'all' ? `_${selectedCategory}` : '';
      const periodSuffix = selectedPeriod === 'today' ? `_${selectedDate}` :
        selectedPeriod === 'month' ? `_${selectedMonth}` :
          selectedPeriod === 'year' ? `_${selectedYear}` : '';
      a.download = `financial_report${categorySuffix}${periodSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export PDF');
    } finally {
      setExporting(null);
    }
  };

  const handleExportExpensesExcel = async () => {
    try {
      const params = new URLSearchParams();
      
      if (selectedPeriod === 'today') {
        params.append('start_date', selectedDate);
        params.append('end_date', selectedDate);
      } else if (selectedPeriod === 'month') {
        const [year, month] = selectedMonth.split('-');
        const start = new Date(parseInt(year), parseInt(month) - 1, 1);
        const end = new Date(parseInt(year), parseInt(month), 0);
        params.append('start_date', format(start, 'yyyy-MM-dd'));
        params.append('end_date', format(end, 'yyyy-MM-dd'));
      } else if (selectedPeriod === 'year') {
        params.append('start_date', `${selectedYear}-01-01`);
        params.append('end_date', `${selectedYear}-12-31`);
      }
      
      const token = localStorage.getItem('access_token');
      const url = `/api/expenses/export/excel${params.toString() ? `?${params.toString()}` : ''}`;
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const periodSuffix = selectedPeriod === 'today' ? `_${selectedDate}` :
          selectedPeriod === 'month' ? `_${selectedMonth}` :
            selectedPeriod === 'year' ? `_${selectedYear}` : '';
        a.download = `expenses_export${periodSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        showToast.error(data.message || 'Failed to export expenses');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export expenses');
    }
  };

  const handleExportExpensesPDF = async () => {
    try {
      const params = new URLSearchParams();
      
      if (selectedPeriod === 'today') {
        params.append('start_date', selectedDate);
        params.append('end_date', selectedDate);
      } else if (selectedPeriod === 'month') {
        const [year, month] = selectedMonth.split('-');
        const start = new Date(parseInt(year), parseInt(month) - 1, 1);
        const end = new Date(parseInt(year), parseInt(month), 0);
        params.append('start_date', format(start, 'yyyy-MM-dd'));
        params.append('end_date', format(end, 'yyyy-MM-dd'));
      } else if (selectedPeriod === 'year') {
        params.append('start_date', `${selectedYear}-01-01`);
        params.append('end_date', `${selectedYear}-12-31`);
      }
      
      const token = localStorage.getItem('access_token');
      const url = `/api/expenses/export/pdf${params.toString() ? `?${params.toString()}` : ''}`;
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const periodSuffix = selectedPeriod === 'today' ? `_${selectedDate}` :
          selectedPeriod === 'month' ? `_${selectedMonth}` :
            selectedPeriod === 'year' ? `_${selectedYear}` : '';
        a.download = `expenses_export${periodSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        showToast.error(data.message || 'Failed to export expenses');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export expenses');
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting('excel');

      const params: any = { type: selectedPeriod };
      if (selectedPeriod === 'today') {
        params.date = selectedDate;
      } else if (selectedPeriod === 'month') {
        params.month = selectedMonth;
      } else if (selectedPeriod === 'year') {
        params.year = parseInt(selectedYear);
      }
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const blob = await exportService.exportFinanceExcel(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const categorySuffix = selectedCategory !== 'all' ? `_${selectedCategory}` : '';
      const periodSuffix = selectedPeriod === 'today' ? `_${selectedDate}` :
        selectedPeriod === 'month' ? `_${selectedMonth}` :
          selectedPeriod === 'year' ? `_${selectedYear}` : '';
      a.download = `financial_report${categorySuffix}${periodSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export Excel');
    } finally {
      setExporting(null);
    }
  };

  if (loading && !summary) {
    return <Loading fullScreen text="Loading finance data..." />;
  }

  const currentData = getCurrentData();

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Finance & Reports</h1>

      {/* Period Selector */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex space-x-2">
              <button
                className={`px-4 py-2 rounded-lg font-medium ${selectedPeriod === 'today'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setSelectedPeriod('today')}
              >
                Today
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium ${selectedPeriod === 'month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setSelectedPeriod('month')}
              >
                This Month
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium ${selectedPeriod === 'year'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setSelectedPeriod('year')}
              >
                This Year
              </button>
            </div>
            {selectedPeriod === 'today' && (
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-48"
              />
            )}
            {selectedPeriod === 'month' && (
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-48"
              />
            )}
            {selectedPeriod === 'year' && (
              <Input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                placeholder="Year"
                min="2020"
                max={new Date().getFullYear()}
                className="w-32"
              />
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Items</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {currentData && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleExportPDF}
                disabled={exporting === 'pdf'}
              >
                {exporting === 'pdf' ? 'Exporting...' : '📄 Export PDF'}
              </Button>
              <Button
                variant="secondary"
                onClick={handleExportExcel}
                disabled={exporting === 'excel'}
              >
                {exporting === 'excel' ? 'Exporting...' : '📊 Export Excel'}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Financial Summary Cards */}
      {currentData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="text-center">
              <p className="text-sm font-medium text-green-700 mb-2">Total Revenue</p>
              <p className="text-3xl font-bold text-green-700">
                {formatCurrency(currentData.total_revenue)}
              </p>
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700 mb-2">Total Cost</p>
              <p className="text-3xl font-bold text-gray-800">
                {formatCurrency(currentData.total_cost)}
              </p>
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-emerald-100 border-green-300">
            <div className="text-center">
              <p className="text-sm font-medium text-green-700 mb-2">Total Profit</p>
              <p className="text-3xl font-bold text-green-700">
                {formatCurrency(currentData.total_profit || (currentData.profit_loss > 0 ? currentData.profit_loss : 0))}
              </p>
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-300">
            <div className="text-center">
              <p className="text-sm font-medium text-red-700 mb-2">Total Loss</p>
              <p className="text-3xl font-bold text-red-700">
                {formatCurrency(currentData.total_loss || (currentData.profit_loss < 0 ? Math.abs(currentData.profit_loss) : 0))}
              </p>
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="text-center">
              <p className="text-sm font-medium text-blue-700 mb-2">Total Sales</p>
              <p className="text-3xl font-bold text-blue-700">
                {currentData.total_sales || 0}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Expenses Section */}
      <Card 
        title="Expenses Overview"
        headerAction={
          expenseSummary && (
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportExpensesExcel}
                disabled={expensesLoading}
                title="Export Expenses to Excel"
              >
                📊 Excel
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportExpensesPDF}
                disabled={expensesLoading}
                title="Export Expenses to PDF"
              >
                📄 PDF
              </Button>
            </div>
          )
        }
        className="mb-6"
      >
        {expensesLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading expenses...</p>
          </div>
        ) : expenseSummary ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-gray-600 mb-1">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(expenseSummary.total_amount)}
                </p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <p className="text-sm text-gray-600 mb-1">Number of Expenses</p>
                <p className="text-2xl font-bold text-orange-600">{expenseSummary.count}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-sm text-gray-600 mb-1">Average per Day</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(expenseSummary.average_per_day)}
                </p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <p className="text-sm text-gray-600 mb-1">Days with Expenses</p>
                <p className="text-2xl font-bold text-indigo-600">{expenseSummary.days_count}</p>
              </div>
            </div>

            {/* Net Profit Calculation */}
            {currentData && (
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-lg border border-emerald-300">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Net Profit (After Expenses)</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      {formatCurrency((currentData.total_revenue || 0) - expenseSummary.total_amount)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Revenue: {formatCurrency(currentData.total_revenue || 0)}</p>
                    <p className="text-sm text-red-600">Expenses: {formatCurrency(expenseSummary.total_amount)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Expense Breakdown by Type */}
            {Object.keys(expenseSummary.type_breakdown).length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Expenses by Type</h3>
                <div className="space-y-2">
                  {Object.entries(expenseSummary.type_breakdown)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([type, data]) => (
                      <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                            {type}
                          </span>
                          <span className="text-sm text-gray-600">{data.count} expenses</span>
                        </div>
                        <span className="text-lg font-semibold text-red-600">
                          {formatCurrency(data.total)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No expenses data available</p>
          </div>
        )}
      </Card>

      {/* Date Range Analysis */}
      <Card title="Custom Date Range Analysis" className="mb-6">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <Input
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
          <Input
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
          <Button onClick={loadDateRange}>Analyze Range</Button>
        </div>

        {rangeData && (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-700">Revenue</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(rangeData.total_revenue)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700">Cost</p>
                <p className="text-2xl font-bold text-gray-800">
                  {formatCurrency(rangeData.total_cost)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-lg border border-green-300">
                <p className="text-sm font-medium text-green-700">Total Profit</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(rangeData.total_profit || (rangeData.profit_loss > 0 ? rangeData.profit_loss : 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg border border-red-300">
                <p className="text-sm font-medium text-red-700">Total Loss</p>
                <p className="text-2xl font-bold text-red-700">
                  {formatCurrency(rangeData.total_loss || (rangeData.profit_loss < 0 ? Math.abs(rangeData.profit_loss) : 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-700">Total Sales</p>
                <p className="text-2xl font-bold text-blue-700">
                  {rangeData.total_sales || 0}
                </p>
              </div>
            </div>

            {rangeData.daily_breakdown && rangeData.daily_breakdown.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Daily Financial Trends</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={getChartData()}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6B7280" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#6B7280" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#059669" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#059669" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
                    <XAxis
                      dataKey="date"
                      stroke="#6B7280"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis
                      stroke="#6B7280"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => `Ksh ${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(Math.round(value))}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#10B981"
                      strokeWidth={3}
                      name="Revenue"
                      dot={{ fill: '#10B981', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cost"
                      stroke="#6B7280"
                      strokeWidth={3}
                      name="Cost"
                      dot={{ fill: '#6B7280', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      stroke="#059669"
                      strokeWidth={3}
                      name="Profit"
                      dot={{ fill: '#059669', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Overall Summary */}
      {summary && currentData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Revenue vs Cost Analysis" className="shadow-lg">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={[
                  {
                    name: 'Revenue',
                    value: currentData.total_revenue || 0,
                  },
                  {
                    name: 'Cost',
                    value: currentData.total_cost || 0,
                  },
                ]}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorRevenueBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0.9} />
                  </linearGradient>
                  <linearGradient id="colorCostBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6B7280" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#4B5563" stopOpacity={0.9} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
                <XAxis
                  dataKey="name"
                  stroke="#6B7280"
                  style={{ fontSize: '12px', fontWeight: '500' }}
                />
                <YAxis
                  stroke="#6B7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `Ksh ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(value: number) => formatCurrency(Math.round(value))}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                >
                  <Cell fill="url(#colorRevenueBar)" />
                  <Cell fill="url(#colorCostBar)" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Profit vs Loss Breakdown" className="shadow-lg">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={[
                  {
                    name: 'Profit',
                    value: currentData.total_profit || (currentData.profit_loss > 0 ? currentData.profit_loss : 0),
                  },
                  {
                    name: 'Loss',
                    value: currentData.total_loss || (currentData.profit_loss < 0 ? Math.abs(currentData.profit_loss) : 0),
                  },
                ]}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorProfitBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0.9} />
                  </linearGradient>
                  <linearGradient id="colorLossBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#DC2626" stopOpacity={0.9} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
                <XAxis
                  dataKey="name"
                  stroke="#6B7280"
                  style={{ fontSize: '12px', fontWeight: '500' }}
                />
                <YAxis
                  stroke="#6B7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `Ksh ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(value: number) => formatCurrency(Math.round(value))}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                >
                  <Cell fill="url(#colorProfitBar)" />
                  <Cell fill="url(#colorLossBar)" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Revenue Distribution" className="shadow-lg">
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <defs>
                  <linearGradient id="colorRevenuePie" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.9} />
                  </linearGradient>
                  <linearGradient id="colorCostPie" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#DC2626" stopOpacity={0.9} />
                  </linearGradient>
                </defs>
                <Pie
                  data={[
                    { name: 'Revenue', value: currentData.total_revenue || 0 },
                    { name: 'Cost', value: currentData.total_cost || 0 },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(props: any) => `${props.name}: ${(props.percent * 100).toFixed(1)}%`}
                  outerRadius={100}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  <Cell fill="url(#colorRevenuePie)" />
                  <Cell fill="url(#colorCostPie)" />
                </Pie>
                <Tooltip
                  formatter={(value: number) => formatCurrency(Math.round(value))}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Profit & Loss Comparison" className="shadow-lg">
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <defs>
                  <linearGradient id="colorProfitPie" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.9} />
                  </linearGradient>
                  <linearGradient id="colorLossPie" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#DC2626" stopOpacity={0.9} />
                  </linearGradient>
                </defs>
                <Pie
                  data={[
                    {
                      name: 'Profit',
                      value: currentData.total_profit || (currentData.profit_loss > 0 ? currentData.profit_loss : 0)
                    },
                    {
                      name: 'Loss',
                      value: currentData.total_loss || (currentData.profit_loss < 0 ? Math.abs(currentData.profit_loss) : 0)
                    },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(props: any) =>
                    props.value > 0 ? `${props.name}: ${(props.percent * 100).toFixed(1)}%` : ''
                  }
                  outerRadius={100}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  <Cell fill="url(#colorProfitPie)" />
                  <Cell fill="url(#colorLossPie)" />
                </Pie>
                <Tooltip
                  formatter={(value: number) => formatCurrency(Math.round(value))}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Item Breakdown by Category */}
      {currentData && currentData.item_breakdown && currentData.item_breakdown.length > 0 && (
        <Card title="Item Breakdown by Category" className="mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loss
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentData.item_breakdown.map((category, index) => (
                  <React.Fragment key={index}>
                    <tr className="bg-blue-50 hover:bg-blue-100">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-bold text-blue-900">{category.category}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                        {formatCurrency(category.total_revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatCurrency(category.total_cost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                        {formatCurrency(category.total_profit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                        {formatCurrency(category.total_loss)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {category.total_quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {category.total_sales}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr key={`${index}-${itemIndex}`} className="hover:bg-gray-50">
                        <td className="px-6 py-2 pl-12 text-sm text-gray-600">
                          <div className="flex flex-col">
                            <span className="font-medium">{item.item_name}</span>
                            <span className="text-xs text-gray-500">{item.item_brand}</span>
                          </div>
                        </td>
                        <td className="px-6 py-2 text-sm text-green-600">
                          {formatCurrency(item.total_revenue)}
                        </td>
                        <td className="px-6 py-2 text-sm text-gray-600">
                          {formatCurrency(item.total_cost)}
                        </td>
                        <td className="px-6 py-2 text-sm text-green-600">
                          {formatCurrency(item.total_profit)}
                        </td>
                        <td className="px-6 py-2 text-sm text-red-600">
                          {formatCurrency(item.total_loss)}
                        </td>
                        <td className="px-6 py-2 text-sm text-gray-600">
                          {item.total_quantity}
                        </td>
                        <td className="px-6 py-2 text-sm text-gray-600">
                          {item.total_sales}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
